<template>
  <div>

    <template v-if="isEntryRoute || isDetailRouteWithoutParameter">
      <entity-entry-header :entityKey="eCurrent.key" :title="$t(`views.${eCurrent.key}.title`)"/>
    </template>

    <!-- if this is active route (e.g. /users) -->
    <template v-if="isEntryRoute">
      <b-row class="mb-3" align-h="between">
        <b-col cols="auto"></b-col>
        <b-col cols="auto">
          <b-button class="mr-3" variant="outline-primary" @click="exportUsers" :disabled="isExporting"><b-icon icon="b-download"/> {{ $t('views.users.button.exportusers') }}</b-button>
          <b-button variant="outline-primary" @click="navigate('Users.Add')"><b-icon icon="plus"/> {{ $t('views.users.button.adduser') }}</b-button>
        </b-col>
      </b-row>

      <entity-entry-table :entityKey="eCurrent.key" :tableSettings="table" searchable>
      </entity-entry-table>
    </template>

    <!-- if subroute is active route (e.g. /users/detail/:userid) -->
    <section v-else-if="isDetailRouteWithParameter">
      <entity-detail-header :entityKey="eCurrent.key" :entityId="eCurrent.id">
        <template #default="data">
          <dl class="detailheader-list">
            <dt class="sr-only">{{ $t('components.entity.detailheader.name.label') }}</dt>
            <dd><h1>{{ data.label }}</h1></dd>

            <div class="w-100"></div>
            <dt>{{ data.definition.$properties.email.translations[$i18n.locale].name }}</dt>
            <dd>{{ data.entity.email }}</dd>
            <div class="w-100"></div>
            <dt>{{ data.definition.$properties.customerNumber.translations[$i18n.locale].name }}</dt>
            <dd>{{ data.entity.customerNumber }}</dd>
          </dl>
        </template>
      </entity-detail-header>

      <router-view/>
    </section>

    <!-- if subroute is active route (e.g. /users/add) -->
    <router-view v-else-if="isDetailRouteWithoutParameter"/>
  </div>
</template>

<script>
import { CLASS_TABLE_FIELD_KEY_UNKNOWN } from '@/constants'

import { TableHelper } from '@/assets/js/helper/table'

import EntityDetailHeader from '@/components/entity/DetailHeader'
import EntityEntryHeader from '@/components/entity/EntryHeader'
import EntityEntryTable from '@/components/entity/EntryTable'
import MixinEntityBase from '@/components/mixins/EntityBase'

import http from '@/$plugins/http'
import { searchParameters } from '@/assets/js/helper/search'
import { getFilenameFromContentDisposition } from '@/assets/js/helper/url'

const ENTITY_KEY = 'users'

export default {
  name: 'Users',
  mixins: [MixinEntityBase],
  components: {
    EntityEntryHeader,
    EntityEntryTable,
    EntityDetailHeader
  },
  data () {
    return {
      isExporting: false,
      entityKey: ENTITY_KEY,
      table: {
        helper: TableHelper,
        mapper: this.tableEntitiesMapper,
        options: {
          fields: {
            sorting: [CLASS_TABLE_FIELD_KEY_UNKNOWN],
            labelKey: `views.${this.$options.name.toLowerCase()}.table.label.{field}`,
            class: {
              requiredactions: 'cell-align-right'
            },
            thClass: {
              requiredactions: 'cell-min cell-nowrap'
            }
          }
        },
        events: {
          rowClicked: this.tableRowClicked
        }
      }
    }
  },
  computed: {
    isEntryRoute () {
      return Object.values(this.$route.matched[this.$route.matched.length - 1].components).find(c => c.name === this.$options.name) !== undefined
    },
    isDetailRouteWithParameter () {
      return this.$route.matched[this.$route.matched.length - 1].path.indexOf(`/:${this.eCurrent.paramKey}`) >= 0
    },
    isDetailRouteWithoutParameter () {
      return !this.isEntryRoute && !this.isDetailRouteWithParameter
    }
  },
  methods: {
    tableEntitiesMapper (entities, definition) {
      return entities
        .map(e => Object.assign({}, e, {
          $routerLink: {
            name: `${this.$options.name}.Detail`,
            params: {
              [this.eCurrent.paramKey]: e.id
            }
          }
        }))
    },
    tableRowClicked (entity) {
      this.$router.push(entity.$routerLink)
    },
    async exportUsers () {
      this.isExporting = true
      const sorts = this.$store.getters[`${this.eCurrent.key}/sorts`]
      const filters = this.$store.getters[`${this.eCurrent.key}/filters`]
      const language = this.$store.getters['gui/getLanguage']
      const parameters = searchParameters({ filters: filters, sorts: sorts })
      const data = {
        language: language,
        filters: parameters.filters,
        sort: parameters.sorts,
        page: parameters.page.number,
        pageSize: parameters.page.size
      }
      http({
        method: 'post',
        url: `/customer/api/${this.eCurrent.key}/exportusers`,
        data: data,
        responseType: 'blob'
      }).then(response => {
        const fileNameHeader = 'content-disposition'
        const suggestedFileName = response.headers[fileNameHeader]
        const fileName = getFilenameFromContentDisposition(suggestedFileName)
        const effectiveFileName = (fileName || 'Users.xlsx')
        const url = window.URL.createObjectURL(response.data)
        const a = document.createElement('a')
        a.href = url
        a.download = effectiveFileName
        document.body.appendChild(a)
        a.click()
      }).catch(error => {
        console.log(error)
      })
      this.isExporting = false
    }
  },
  created () {
    TableHelper.rowClassHook(entity => entity.$routerLink ? 'has-routerlink' : null)
  }
}
</script>

<style lang="scss"></style>
